import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';

type Category = 'bug' | 'feature' | 'general';

const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'bug',     label: 'Bug Report'       },
  { id: 'feature', label: 'Feature Request'  },
  { id: 'general', label: 'General Feedback' },
];

const PRIMARY = 'var(--color-primary)';
const BORDER  = 'var(--color-border)';
const MUTED   = 'var(--color-text-muted)';
const SURFACE = 'var(--color-surface)';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const [category, setCategory]       = useState<Category>('general');
  const [message, setMessage]         = useState('');
  const [rating, setRating]           = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Focus textarea when modal opens
  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 50);
  }, [open]);

  // Auto-close 2s after success
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(handleClose, 2000);
    return () => clearTimeout(t);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const handleClose = () => {
    setCategory('general');
    setMessage('');
    setRating(null);
    setHoverRating(null);
    setError(null);
    setSuccess(false);
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (message.trim().length < 10) {
      setError('Please write at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You must be signed in to send feedback.');
        setSubmitting(false);
        return;
      }
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ category, message: message.trim(), rating }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  const charCount = message.length;
  const charOver  = charCount > 1000;

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    border: `1.5px solid ${active ? PRIMARY : BORDER}`,
    borderRadius: 999,
    backgroundColor: active ? 'var(--color-success-light)' : 'transparent',
    color: active ? PRIMARY : MUTED,
    cursor: 'pointer',
  });

  // ── Success state ──────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 20,
      }}>
        <div style={{
          backgroundColor: 'var(--color-background)', borderRadius: 20,
          padding: '36px 28px', maxWidth: 360, width: '100%',
          textAlign: 'center', boxShadow: '0 20px 50px rgba(31,76,64,0.2)',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            backgroundColor: 'var(--color-success-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 24, color: PRIMARY, fontWeight: 700,
          }}>
            ✓
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px', color: 'var(--color-text)' }}>
            Thanks for your feedback!
          </h2>
          <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
            We read every submission.
          </p>
        </div>
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 20,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)', borderRadius: 20,
          padding: '28px 24px', maxWidth: 440, width: '100%',
          boxShadow: '0 20px 50px rgba(31,76,64,0.2)', position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={handleClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 16, right: 16,
            background: 'transparent', border: 'none',
            cursor: 'pointer', fontSize: 18, color: MUTED, lineHeight: 1, padding: 4,
          }}
        >
          ✕
        </button>

        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 4px', color: 'var(--color-text)' }}>
          Send Feedback
        </h2>
        <p style={{ fontSize: 13, color: MUTED, margin: '0 0 20px' }}>
          Help us improve Phrenos.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Category pills */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                type="button"
                style={pillStyle(category === c.id)}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => { setMessage(e.target.value); setError(null); }}
            placeholder="What's on your mind? (min 10 characters)"
            maxLength={1100}
            rows={5}
            style={{
              width: '100%', padding: '12px 14px', fontSize: 14, lineHeight: 1.5,
              border: `1.5px solid ${BORDER}`, borderRadius: 12,
              backgroundColor: SURFACE, color: 'var(--color-text)',
              resize: 'vertical', fontFamily: 'inherit', outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Char counter */}
          <div style={{
            textAlign: 'right', fontSize: 12, marginTop: 4,
            color: charOver ? 'var(--color-danger)' : MUTED,
          }}>
            {charCount} / 1000
          </div>

          {/* Star rating */}
          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 13, color: MUTED, margin: '0 0 8px' }}>
              Overall rating <span style={{ fontWeight: 400 }}>(optional)</span>
            </p>
            <div style={{ display: 'flex', gap: 4 }}>
              {[1, 2, 3, 4, 5].map(star => {
                const filled = star <= (hoverRating ?? rating ?? 0);
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(prev => prev === star ? null : star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(null)}
                    aria-label={`${star} star${star !== 1 ? 's' : ''}`}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: 28, padding: 2, lineHeight: 1,
                      color: filled ? '#D97706' : BORDER,
                      transition: 'color 0.1s',
                    }}
                  >
                    ★
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p style={{
              fontSize: 13, color: 'var(--color-danger)', margin: '12px 0 0',
              padding: '10px 12px', backgroundColor: 'var(--color-surface)', borderRadius: 10,
              border: '1px solid var(--color-danger)',
            }}>
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || charOver}
            style={{
              width: '100%', padding: '13px 0', marginTop: 16,
              backgroundColor: PRIMARY, color: '#fff',
              border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
              cursor: submitting || charOver ? 'not-allowed' : 'pointer',
              opacity: submitting || charOver ? 0.7 : 1,
            }}
          >
            {submitting ? 'Sending…' : 'Send Feedback'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
